import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import Button, { LoadingButton } from '@atlaskit/button';
import { Checkbox } from '@atlaskit/checkbox';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import InlineMessage from '@atlaskit/inline-message';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import { R400 } from '@atlaskit/theme/colors';
import Tooltip from '@atlaskit/tooltip';
import AddIcon from '@atlaskit/icon/glyph/add';
import ExportIcon from '@atlaskit/icon/glyph/export';
import {
  BillingAdminPerson,
  CustomSearch,
  DynamicResultTable,
  MoreActions,
  OpenSidebarButton,
  useFeatureToggle,
  useUpdateTransactionAccount,
} from '@css-xp/base';
import { emptyStateProp } from '@css-xp/ppc-common';
import { UpdateTransactionAccountPayload } from '@atlassian/cssxp-types-billing';
import { useBillingAdminsCSV } from '../../hooks/use-billing-admins-csv';
import { ENABLE_DESIGN_ITERATION_1, STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants';
import {
  ADVOCATE_ACCESS_TYPE,
  ADVOCATE_RESOURCE_TYPE,
  AdvocateAccessWall,
  useAdvocateAccessControl,
} from '@css-xp/ppc-access-control';
import { emptyStatePropForAdminsTab, emptyStatePropForWrongSearch } from '@css-xp/ppc-common/src/state-props';
import { SortDirection } from '@css-xp/ppc-graphtypes';
import { SIDEBAR } from '../../detail-page';
import { BillingAdminsRemoveButton } from './billing-admins-remove-button';
import './billing-admins.scss';

const DEFAULT_ROWS_PER_PAGE = 8;
const DEFAULT_PAGE = 1;

const wrapInCell = (node: React.ReactElement | string) => {
  return <div>{node}</div>;
};

const getDataForCurrentPage = (data: Admin[], currentPage: number, pageSize: number) => {
  /** updated the logic of frontend pagination so as to handle the case when the filtered data after the searched input
   * is less than the pageSize and currentPage is not 1.
   * For instance : currentPage = 2, pageSize = 10, data.length = 5,
   * start was 8 and end was 16 initially which will always render empty state.
   * This logic has no effect in the current state when design iteration-1 FF is off
   **/
  let start = (currentPage - 1) * pageSize;
  if (start > data.length) {
    start = 0;
  }
  const end = Math.min(start + pageSize, data.length);
  return data.slice(start, end);
};

const sortAdmins = (admins: Admin[], sortOrder: SortDirection) => {
  if (sortOrder === SortDirection.ASC) {
    return admins.sort((admin1, admin2) => admin1.email.localeCompare(admin2.email));
  }

  return admins.sort((admin1, admin2) => admin2.email.localeCompare(admin1.email));
};

interface CheckBoxStateType {
  email: string;
  isChecked: boolean;
  display: boolean;
}

interface FormData {
  searchTerm: string;
}

export interface Admin {
  fullName: string;
  email: string;
  isPartnerAdmin: boolean;
  picture: string;
  atlassianAccountId: string;
}

interface BillingAdminsProps {
  billingAdmins: Admin[];
  loading: boolean;
  transactionAccountId: string;
  refetch: () => void;
  isRemoveDisabled: boolean;
  validDomains: string[];
  beforeTable?: React.ReactElement | null;
  partnerAccountId?: string;
  version?: number;
}

enum BillingAdminTableHeader {
  NAME = 'Billing admin',
  ACTIONS = 'Actions',
}

export const BillingAdmins = ({
  billingAdmins: billingAdminsData,
  loading,
  transactionAccountId,
  refetch,
  isRemoveDisabled,
  validDomains,
  beforeTable,
  partnerAccountId,
  version,
}: BillingAdminsProps) => {
  const [isEnabled] = useFeatureToggle();
  const billingAdmins = sortAdmins(billingAdminsData, SortDirection.ASC);

  const [rowsPerPage, setRowsPerPage] = useState<number>(DEFAULT_ROWS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState<number>(DEFAULT_PAGE);

  const [admins, setBillingAdmins] = useState<Admin[]>(getDataForCurrentPage(billingAdmins, currentPage, rowsPerPage));

  const [emailBeingRemoved, setEmailBeingRemoved] = useState<string[]>([]);
  const [adminsCheckboxState, setAdminsCheckBoxState] = useState<CheckBoxStateType[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [total, setTotalCount] = useState<number>(0);
  const [sortOrder, setSortOrder] = useState<SortDirection>(SortDirection.ASC);

  const [isOpen, setIsOpen] = useState(false);

  const canLoggedInUserRemoveBillingAdmin = useAdvocateAccessControl({
    accessType: ADVOCATE_ACCESS_TYPE.DELETE,
    resource: ADVOCATE_RESOURCE_TYPE.BILLING_ADMIN,
  });

  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
  const isRedesignFastFollowEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_REDESIGN_FAST_FOLLOW);

  const filterBillingAdminsFromSearchedInput = (admins: Admin[], searchValue: string) => {
    const escapedvalue = searchValue.trim().replace(/[+]/g, '\\$&'); // escape special character + in search input
    const regexPattern = `\\s*${escapedvalue}\\s*`;

    return admins.filter((admin) => {
      const hasNameMatched = new RegExp(regexPattern, 'ig').test(admin.fullName);
      const hasEmailMatched = new RegExp(regexPattern, 'ig').test(admin.email);
      return hasNameMatched || hasEmailMatched;
    });
  };

  useEffect(() => {
    if (!loading) {
      const filteredBillingAdmins = sortAdmins(
        filterBillingAdminsFromSearchedInput(billingAdmins, searchInput),
        sortOrder
      );

      setTotalCount(filteredBillingAdmins.length);
      setBillingAdmins(getDataForCurrentPage(filteredBillingAdmins, currentPage, rowsPerPage));
      setAdminsCheckBoxState(
        billingAdmins?.map((billingAdmin) => ({ email: billingAdmin.email, isChecked: false, display: false }))
      );
    }
  }, [billingAdmins, currentPage, loading, rowsPerPage, searchInput, total, sortOrder]);

  const isVersion2 = version === 2;

  const { isProcessing, updateTransactionAccount } = useUpdateTransactionAccount({
    // eslint-disable-next-line
    onClose: () => {
      //do nothing
    },
    onComplete: () => {
      refetch && refetch();
    },
    notification: {
      successHeading: 'Update successful',
      successMessage: isVersion2 ? 'Billing admin removed' : 'Billing admins removed',
    },
  });

  const { downloadBillingAdminsCSV, billingAdminsDataLoading } = useBillingAdminsCSV({
    transactionAccountId,
    partnerAccountId: partnerAccountId || '',
  });

  const openModal = useCallback((emailsToRemove: string[]) => {
    setIsOpen(true);
    setEmailBeingRemoved((val) => [...val, ...emailsToRemove.filter((el) => el !== '')]);
  }, []);

  const closeModal = useCallback(() => setIsOpen(false), []);

  const handlePageChange = (page: number) => {
    setBillingAdmins(getDataForCurrentPage(billingAdmins, page, rowsPerPage));
    setCurrentPage(page);
  };

  const handleRowsCountChange = (rowsCount: number) => {
    setRowsPerPage(rowsCount);
    setCurrentPage(1);
    setBillingAdmins(getDataForCurrentPage(billingAdmins, 1, rowsCount));
  };

  const removeBillingAdmin = useCallback(() => {
    const updateTransactionAccountPayload: UpdateTransactionAccountPayload = {
      modifiedBillingAdmins: {
        emailsOrAaidsToDelete: emailBeingRemoved as string[],
      },
    };
    updateTransactionAccount({
      transactionAccountId,
      updateTransactionAccountPayload,
    });
    closeModal();
  }, [closeModal, emailBeingRemoved, transactionAccountId, updateTransactionAccount]);

  const handleAdminSelect = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      const clonedIsCheckedArray = [...adminsCheckboxState];
      if (event.target.checked) {
        const selectedIndex = clonedIsCheckedArray.findIndex((el) => el.email === value);
        clonedIsCheckedArray[selectedIndex] = { email: value, isChecked: true, display: true };
        setAdminsCheckBoxState(clonedIsCheckedArray);
      } else {
        const selectedIndex = clonedIsCheckedArray.findIndex((el) => el.email === value);
        clonedIsCheckedArray[selectedIndex] = { email: value, isChecked: false, display: false };
        setAdminsCheckBoxState(clonedIsCheckedArray);
      }
    },
    [adminsCheckboxState]
  );

  const handleCheckboxClickInModal = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      const clonedIsCheckedArray = [...adminsCheckboxState];
      const clonedEmailBeingRemoved = [...emailBeingRemoved];

      if (event.target.checked) {
        const selectedIndex = clonedIsCheckedArray.findIndex((el) => el.email === value);
        clonedIsCheckedArray[selectedIndex] = { email: value, isChecked: true, display: true };
        setAdminsCheckBoxState(clonedIsCheckedArray);
        setEmailBeingRemoved([...emailBeingRemoved, value]);
      } else {
        const selectedIndex = clonedIsCheckedArray.findIndex((el) => el.email === value);
        clonedIsCheckedArray[selectedIndex] = { email: value, isChecked: false, display: true };
        setAdminsCheckBoxState(clonedIsCheckedArray);
        clonedEmailBeingRemoved.splice(clonedEmailBeingRemoved.indexOf(value), 1);
        setEmailBeingRemoved(clonedEmailBeingRemoved);
      }
    },
    [adminsCheckboxState, emailBeingRemoved]
  );

  const handleSingleAdminDelete = useCallback(
    (emailToRemove: string): void => {
      const updateTransactionAccountPayload: UpdateTransactionAccountPayload = {
        modifiedBillingAdmins: {
          emailsOrAaidsToDelete: [emailToRemove],
        },
      };
      updateTransactionAccount({
        transactionAccountId,
        updateTransactionAccountPayload,
      });
    },
    [transactionAccountId, updateTransactionAccount]
  );

  const makeHead = () => {
    if (total === 0) {
      return { cells: [] };
    }

    const cells = [
      {
        key: BillingAdminTableHeader.NAME,
        content: wrapInCell(BillingAdminTableHeader.NAME),
        isSortable: isDesignIteration1Enabled,
      },
      {
        content: wrapInCell(BillingAdminTableHeader.ACTIONS),
      },
    ];
    return {
      cells,
    };
  };

  const makeRows = useCallback(() => {
    const isOnlySingleAdmin = billingAdmins?.length === 1;
    return admins.map((billingAdmin: Admin, index: number) => {
      const rowKey = `billing-admin-row-${index}`;
      const isRemoveBillingAdminDisabled = billingAdmin.isPartnerAdmin || isRemoveDisabled || isOnlySingleAdmin;
      return {
        key: rowKey,
        cells: [
          {
            key: `${rowKey}-details`,
            content: (
              <div
                className={classNames('bulk-admin-removal-container', {
                  gap3: isVersion2,
                })}
              >
                {!isRemoveDisabled && !isOnlySingleAdmin ? (
                  <Checkbox
                    testId={`admin-checkbox-${index}`}
                    isChecked={
                      adminsCheckboxState[adminsCheckboxState.findIndex((el) => el.email === billingAdmin.email)]
                        ?.isChecked
                    }
                    onChange={handleAdminSelect}
                    value={billingAdmin.email as string}
                    isDisabled={billingAdmin.isPartnerAdmin as boolean}
                    name={`${rowKey}-remove-admin`}
                    className="remove-bulk-admin-checkbox"
                  />
                ) : null}
                <BillingAdminPerson
                  fullName={billingAdmin.fullName}
                  email={billingAdmin.email}
                  isPartnerAdmin={billingAdmin.isPartnerAdmin}
                  picture={billingAdmin.picture}
                  version={version}
                />
              </div>
            ),
          },
          ...(isVersion2
            ? [
                {
                  key: `${rowKey}-actions`,
                  content:
                    isRedesignFastFollowEnabled && canLoggedInUserRemoveBillingAdmin ? (
                      <BillingAdminsRemoveButton
                        billingAdmin={billingAdmin}
                        isDisabled={isRemoveBillingAdminDisabled}
                        isLoading={isProcessing}
                        onRemoveAdmin={handleSingleAdminDelete}
                      />
                    ) : (
                      <MoreActions
                        actions={...canLoggedInUserRemoveBillingAdmin
                          ? [
                              {
                                id: `delete-${rowKey}`,
                                content: 'Remove',
                                action: () => handleSingleAdminDelete(billingAdmin.email),
                                isDisabled: isRemoveBillingAdminDisabled,
                              },
                            ]
                          : []}
                        testId="billing-admin-remove-action"
                      />
                    ),
                },
              ]
            : []),
        ],
      };
    });
  }, [
    admins,
    isRemoveDisabled,
    version,
    adminsCheckboxState,
    handleAdminSelect,
    handleSingleAdminDelete,
    billingAdmins?.length,
  ]);

  const handleSorting = (data: any) => {
    setSortOrder(data.sortOrder);
    setCurrentPage(DEFAULT_PAGE);
  };

  const isAllAdminsSelected = useMemo(() => adminsCheckboxState.filter((el) => el.isChecked).length === total, [
    adminsCheckboxState,
    total,
  ]);

  const onSearch = useCallback(async (formData: FormData) => {
    const searchInputVal = formData?.searchTerm;
    setSearchInput(searchInputVal);
  }, []);

  const renderBillingAdminSearch = () => {
    if (loading || isProcessing) {
      return null;
    }

    return (
      <div
        className={classNames({
          'billing-admins-search-bar': !isDesignIteration1Enabled,
          'billing-admins-search-bar-v2': isDesignIteration1Enabled,
        })}
      >
        <CustomSearch
          placeholder="Search"
          onSearch={onSearch}
          isClearable={true}
          helperMessage="Enter name or email address"
          isSearchableOnChange={true}
        />
      </div>
    );
  };

  const renderAddAminAction = useCallback(() => {
    return [
      () => (
        <div className={!version ? 'billing-admin-actions add' : undefined}>
          <div className={!version ? 'add-action' : undefined}>
            <AdvocateAccessWall
              accessType={ADVOCATE_ACCESS_TYPE.CREATE}
              resource={ADVOCATE_RESOURCE_TYPE.BILLING_ADMIN}
            >
              <OpenSidebarButton
                sidebar={SIDEBAR.ADD_BILLING_ADMINS}
                sidebarOptions={{
                  sidebarWidth: isDesignIteration1Enabled ? undefined : '600px',
                  isOverlay: !isDesignIteration1Enabled,
                  sidebarTitle: 'Add billing admin',
                }}
                payload={{
                  txaId: transactionAccountId,
                  validDomains,
                  currentUserList: billingAdmins.map((billingAdmin) => billingAdmin.atlassianAccountId),
                  refetch: () => {
                    setSearchInput('');
                    refetch();
                  },
                }}
                iconBefore={<AddIcon label="Add icon" size="small" />}
                testId="add-billing-admins"
                isDisabled={loading}
              >
                Add a billing admin
              </OpenSidebarButton>
            </AdvocateAccessWall>
          </div>
        </div>
      ),
    ];
  }, [total, loading, billingAdmins]);

  const renderRemoveSelectedAdmins = () => {
    return (
      <div className="remove-admin-btn-container">
        {isAllAdminsSelected && (
          <div>
            <ErrorIcon size="small" label="" primaryColor={R400} />
            <p className="remove-admin-warning">There must be at least one billing admin remaining</p>
          </div>
        )}
        {/* Selected count only shown in version 2 and when count > 0 */}
        {isVersion2 && <span>{adminsCheckboxState.filter(({ isChecked }) => isChecked).length} selected</span>}
        <AdvocateAccessWall accessType={ADVOCATE_ACCESS_TYPE.DELETE} resource={ADVOCATE_RESOURCE_TYPE.BILLING_ADMIN}>
          <LoadingButton
            appearance={isVersion2 ? 'default' : 'primary'}
            isLoading={isProcessing}
            isDisabled={isAllAdminsSelected}
            testId="remove-admin-btn"
            onClick={(_) => {
              openModal(adminsCheckboxState.map((checkedArr) => (checkedArr.isChecked ? checkedArr.email : '')));
            }}
          >
            Remove
          </LoadingButton>
        </AdvocateAccessWall>
      </div>
    );
  };

  const renderActionsOnTop = useCallback(() => {
    const isOnlySingleAdmin = total === 1;
    const isAdminSelected = !!adminsCheckboxState.find((el) => el.isChecked);

    return [
      () => {
        const addAdminButton = renderAddAminAction()[0]();

        return (
          <div
            className={isVersion2 ? 'billing-admin-actions-add-container-v2' : 'billing-admin-actions-add-container'}
            data-test-id="billing-admin-actions-add-container"
          >
            {!version && addAdminButton}
            <div className="billing-admin-actions-right-container">
              {!isVersion2 && !isRemoveDisabled && !isOnlySingleAdmin && (
                <>
                  {isAdminSelected ? (
                    renderRemoveSelectedAdmins()
                  ) : (
                    <div className="remove-admin-btn-container">
                      <AdvocateAccessWall
                        accessType={ADVOCATE_ACCESS_TYPE.DELETE}
                        resource={ADVOCATE_RESOURCE_TYPE.BILLING_ADMIN}
                      >
                        <Tooltip content="Select a billing admin first" position="top">
                          <Button testId="remove-admin-btn" appearance="primary" isDisabled>
                            Remove
                          </Button>
                        </Tooltip>
                      </AdvocateAccessWall>
                    </div>
                  )}
                </>
              )}
              {total > 0 && (
                <LoadingButton
                  onClick={() => {
                    downloadBillingAdminsCSV();
                  }}
                  testId="billing-export-as-csv"
                  isLoading={billingAdminsDataLoading}
                  iconBefore={<ExportIcon label="Export icon" size="small" />}
                >
                  Export as CSV
                </LoadingButton>
              )}
              {billingAdminsData?.length > 0 && isVersion2 && addAdminButton}
            </div>
          </div>
        );
      },
    ];
  }, [
    total,
    adminsCheckboxState,
    isRemoveDisabled,
    isAllAdminsSelected,
    isProcessing,
    openModal,
    billingAdminsDataLoading,
    version,
    renderAddAminAction,
    isEnabled,
    downloadBillingAdminsCSV,
  ]);

  const getRemoveBulkAdminModalText = useCallback(() => {
    const selectedAdminsCount = adminsCheckboxState.filter((el) => el.isChecked).length;
    return `Once you confirm, ${selectedAdminsCount} of ${total} billing admins will be removed from this billing account.`;
  }, [adminsCheckboxState, total]);

  const getEmptyStateV2 = () => {
    const [AddBillingAdmin] = renderAddAminAction();
    return billingAdminsData?.length
      ? emptyStatePropForWrongSearch
      : { ...emptyStatePropForAdminsTab, primaryAction: <AddBillingAdmin /> };
  };

  const emptyStateProps = isEnabled(ENABLE_DESIGN_ITERATION_1) ? getEmptyStateV2() : emptyStateProp;

  return (
    <>
      <ModalTransition>
        {isOpen && (
          <Modal onClose={closeModal} testId="remove-billing-admin-modal">
            <ModalHeader>
              <ModalTitle>
                <InlineMessage type="warning" secondaryText={`Remove these billing admins?`} />
              </ModalTitle>
            </ModalHeader>
            <ModalBody>
              <div className="remove-admins-modal-container">
                <p>{getRemoveBulkAdminModalText()}</p>

                <div className="remove-admins-list">
                  <p>Selected billing admins:</p>

                  <div style={{ position: 'relative' }}>
                    <div className="remove-admins-scroll-paper">
                      <div style={{ height: '100%' }}>
                        <ul className="remove-admins-scroll-list">
                          {adminsCheckboxState.map(({ email, display }, index) => {
                            const name = billingAdmins.find((billingAdmin) => billingAdmin.email === email)?.fullName;
                            return (
                              display && (
                                <li data-testid={`selected-admin`} key={index}>
                                  <div className="remove-admins-scroll-list-item">
                                    <Checkbox
                                      testId={`select-admin-modal-${index}`}
                                      isChecked={
                                        adminsCheckboxState[
                                          adminsCheckboxState.findIndex((el: { email: string }) => el.email === email)
                                        ]?.isChecked
                                      }
                                      onChange={handleCheckboxClickInModal}
                                      value={email as string}
                                    />
                                    <p>{`${name}, ${email}`}</p>
                                  </div>
                                </li>
                              )
                            );
                          })}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <div className="remove-modal-footer">
                <Button appearance="subtle" onClick={closeModal}>
                  Cancel
                </Button>
                <Button
                  appearance="warning"
                  onClick={removeBillingAdmin}
                  autoFocus
                  testId="remove-billing-admin-btn"
                  isDisabled={emailBeingRemoved.length === 0}
                >
                  Confirm
                </Button>
              </div>
            </ModalFooter>
          </Modal>
        )}
      </ModalTransition>
      <div
        className={classNames('billing-admins-container', {
          'billing-admins-container-v2': isVersion2,
        })}
      >
        <div className="billing-admins-table">
          <DynamicResultTable
            beforeTable={beforeTable}
            isLoading={isVersion2 ? loading || isProcessing : loading}
            rows={[...makeRows()]}
            rowsPerPage={rowsPerPage}
            currentPage={currentPage}
            loadingSpinnerSize="large"
            emptyStateProps={emptyStateProps}
            totalCount={total}
            onPageChange={handlePageChange}
            isPaginationEnabled={total > DEFAULT_ROWS_PER_PAGE}
            onRowChange={handleRowsCountChange}
            actions={renderActionsOnTop()}
            boxShadow={false}
            className="billing-admins-table"
            head={isVersion2 ? makeHead() : undefined}
            showFilters={true}
            sortKey={isDesignIteration1Enabled && total > 0 ? BillingAdminTableHeader.NAME : undefined}
            sortOrder={isDesignIteration1Enabled ? sortOrder : undefined}
            onSort={isDesignIteration1Enabled ? handleSorting : undefined}
            beforeTableHead={
              isVersion2 && adminsCheckboxState.find((el) => el.isChecked) ? renderRemoveSelectedAdmins() : null
            }
          >
            {billingAdminsData?.length > 0 && renderBillingAdminSearch()}
          </DynamicResultTable>
        </div>
      </div>
    </>
  );
};
